// Generated fallback textures for combat visuals. Shared keys from src/art/keys.ts are used
// when the FX art builder produced them; otherwise these white (tintable) placeholders keep
// every projectile, trail and zone readable. Created once per game (texture manager is global).

import type Phaser from 'phaser';

export const CTEX = {
  soft: 'cmb_soft', // 64×64 radial glow
  streak: 'cmb_streak', // 128×16 trail streak, bright at the right end
  bolt: 'cmb_bolt', // 32×12 energy capsule
  orb: 'cmb_orb', // 32×32 round shot
  rail: 'cmb_rail', // 64×10 long slug
  rocket: 'cmb_rocket', // 28×12 micro-missile
  ring: 'cmb_ring', // 128×128 thin ring
  dot: 'cmb_dot', // 8×8 soft mote
  band: 'cmb_band', // 64×32 ground trail band
} as const;

type Draw = (g: CanvasRenderingContext2D, w: number, h: number) => void;

function make(scene: Phaser.Scene, key: string, w: number, h: number, draw: Draw): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return;
  const g = tex.getContext();
  g.clearRect(0, 0, w, h);
  draw(g, w, h);
  tex.refresh();
}

function radial(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, stops: [number, string][]): void {
  const grad = g.createRadialGradient(cx, cy, 0, cx, cy, r);
  for (const [o, c] of stops) grad.addColorStop(o, c);
  g.fillStyle = grad;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.fill();
}

function capsule(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const r = h / 2;
  g.beginPath();
  g.moveTo(x + r, y);
  g.lineTo(x + w - r, y);
  g.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
  g.lineTo(x + r, y + h);
  g.arc(x + r, y + r, r, Math.PI / 2, (Math.PI * 3) / 2);
  g.closePath();
}

let built = false;

/** Create every fallback texture that does not exist yet. Safe to call repeatedly. */
export function ensureCombatTextures(scene: Phaser.Scene): void {
  if (built && scene.textures.exists(CTEX.band)) return;
  built = true;

  make(scene, CTEX.soft, 64, 64, (g) =>
    radial(g, 32, 32, 32, [
      [0, 'rgba(255,255,255,1)'],
      [0.25, 'rgba(255,255,255,0.75)'],
      [0.55, 'rgba(255,255,255,0.25)'],
      [1, 'rgba(255,255,255,0)'],
    ]),
  );

  make(scene, CTEX.streak, 128, 16, (g, w, h) => {
    const lin = g.createLinearGradient(0, 0, w, 0);
    lin.addColorStop(0, 'rgba(255,255,255,0)');
    lin.addColorStop(0.7, 'rgba(255,255,255,0.55)');
    lin.addColorStop(1, 'rgba(255,255,255,1)');
    g.fillStyle = lin;
    // Soft vertical profile: three stacked bands, brightest in the middle.
    g.globalAlpha = 0.35;
    capsule(g, 0, 0, w, h);
    g.fill();
    g.globalAlpha = 0.65;
    capsule(g, 0, h * 0.25, w, h * 0.5);
    g.fill();
    g.globalAlpha = 1;
    capsule(g, 0, h * 0.4, w, h * 0.2);
    g.fill();
  });

  make(scene, CTEX.bolt, 32, 12, (g, w, h) => {
    g.fillStyle = 'rgba(255,255,255,0.35)';
    capsule(g, 0, 0, w, h);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,0.8)';
    capsule(g, 3, 2.5, w - 6, h - 5);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,1)';
    capsule(g, 8, 4, w - 12, h - 8);
    g.fill();
  });

  make(scene, CTEX.orb, 32, 32, (g) =>
    radial(g, 16, 16, 16, [
      [0, 'rgba(255,255,255,1)'],
      [0.45, 'rgba(255,255,255,1)'],
      [0.62, 'rgba(255,255,255,0.7)'],
      [1, 'rgba(255,255,255,0)'],
    ]),
  );

  make(scene, CTEX.rail, 64, 10, (g, w, h) => {
    g.fillStyle = 'rgba(255,255,255,0.3)';
    capsule(g, 0, 0, w, h);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,0.85)';
    capsule(g, 4, 2, w - 6, h - 4);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,1)';
    capsule(g, 14, 3.5, w - 16, h - 7);
    g.fill();
  });

  make(scene, CTEX.rocket, 28, 12, (g) => {
    // Fins (dim) + body + bright nose; tinted at runtime.
    g.fillStyle = 'rgba(150,150,160,1)';
    g.beginPath();
    g.moveTo(1, 0);
    g.lineTo(9, 4);
    g.lineTo(9, 8);
    g.lineTo(1, 12);
    g.closePath();
    g.fill();
    g.fillStyle = 'rgba(225,228,235,1)';
    capsule(g, 4, 3, 18, 6);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,1)';
    g.beginPath();
    g.moveTo(19, 3);
    g.lineTo(27, 6);
    g.lineTo(19, 9);
    g.closePath();
    g.fill();
  });

  make(scene, CTEX.ring, 128, 128, (g) => {
    g.strokeStyle = 'rgba(255,255,255,0.35)';
    g.lineWidth = 10;
    g.beginPath();
    g.arc(64, 64, 57, 0, Math.PI * 2);
    g.stroke();
    g.strokeStyle = 'rgba(255,255,255,1)';
    g.lineWidth = 3;
    g.beginPath();
    g.arc(64, 64, 57, 0, Math.PI * 2);
    g.stroke();
  });

  make(scene, CTEX.dot, 8, 8, (g) =>
    radial(g, 4, 4, 4, [
      [0, 'rgba(255,255,255,1)'],
      [0.5, 'rgba(255,255,255,0.8)'],
      [1, 'rgba(255,255,255,0)'],
    ]),
  );

  make(scene, CTEX.band, 64, 32, (g, w, h) => {
    const lin = g.createLinearGradient(0, 0, 0, h);
    lin.addColorStop(0, 'rgba(255,255,255,0)');
    lin.addColorStop(0.3, 'rgba(255,255,255,0.45)');
    lin.addColorStop(0.5, 'rgba(255,255,255,1)');
    lin.addColorStop(0.7, 'rgba(255,255,255,0.45)');
    lin.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = lin;
    g.fillRect(0, 0, w, h);
    // A little horizontal texture (tile-safe: symmetric sine) so stretched bands do not look flat.
    g.globalCompositeOperation = 'destination-out';
    for (let x = 0; x < w; x++) {
      const a = 0.18 + 0.18 * Math.sin((x / w) * Math.PI * 6);
      g.fillStyle = `rgba(0,0,0,${a.toFixed(3)})`;
      g.fillRect(x, 0, 1, h);
    }
    g.globalCompositeOperation = 'source-over';
  });
}

/** `key` if that texture exists, else `fallback`. */
export function texOr(scene: Phaser.Scene, key: string | undefined, fallback: string): string {
  return key && scene.textures.exists(key) ? key : fallback;
}
